import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./TOS.scss";

interface TosPopupProps {
    open: boolean;
    onClose: () => void;
}

export default function TOSPopup({ open, onClose }: TosPopupProps) {
    const { t } = useTranslation();

    const getItems = (key: string): string[] => {
        return t(key, {
            returnObjects: true,
        }) as string[];
    };

    if (!open) return null;

    return (
        <div className="tos-popup-overlay" onClick={onClose}>
            <div
                className="tos-popup"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="tos-popup__header">
                    <div>
                        <h2>
                            <span>EnderClub</span> {t("TOS.title")}
                        </h2>

                        <p>{t("TOS.subtitle")}</p>
                    </div>

                    <button
                        type="button"
                        className="tos-popup__close"
                        onClick={onClose}
                        aria-label={t("TOS.close")}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="tos-popup__content">
                    {/* 1. Introduzione */}
                    <section>
                        <h3>{t("TOS.sections.1.title")}</h3>

                        <p>
                            {t("TOS.sections.1.p1_before")}{" "}
                            <strong>{t("TOS.sections.1.brand")}</strong>
                            {t("TOS.sections.1.p1_after")}
                        </p>

                        <p>{t("TOS.sections.1.p2")}</p>
                    </section>

                    {/* 2. Servizi offerti */}
                    <section>
                        <h3>{t("TOS.sections.2.title")}</h3>

                        <p>{t("TOS.sections.2.p1")}</p>

                        <ul>
                            {getItems("TOS.sections.2.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>

                        <p>{t("TOS.sections.2.p2")}</p>
                    </section>

                    {/* 3. Rapporto con il cliente */}
                    <section>
                        <h3>{t("TOS.sections.3.title")}</h3>

                        <p>{t("TOS.sections.3.p1")}</p>

                        <p>{t("TOS.sections.3.p2")}</p>

                        <ul>
                            {getItems("TOS.sections.3.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>

                        <p>{t("TOS.sections.3.p3")}</p>
                    </section>

                    {/* 4. Accesso agli account social */}
                    <section>
                        <h3>{t("TOS.sections.4.title")}</h3>

                        <p>{t("TOS.sections.4.p1")}</p>

                        <p>{t("TOS.sections.4.p2")}</p>

                        <p>{t("TOS.sections.4.p3")}</p>
                    </section>

                    {/* 5. Creazione dei contenuti */}
                    <section>
                        <h3>{t("TOS.sections.5.title")}</h3>

                        <p>{t("TOS.sections.5.p1")}</p>

                        <p>{t("TOS.sections.5.p2")}</p>
                    </section>

                    {/* 6. Proprietà dei contenuti */}
                    <section>
                        <h3>{t("TOS.sections.6.title")}</h3>

                        <ul>
                            {getItems("TOS.sections.6.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>
                    </section>

                    {/* 7. Pagamenti */}
                    <section>
                        <h3>{t("TOS.sections.7.title")}</h3>

                        <p>{t("TOS.sections.7.p1")}</p>

                        <p>{t("TOS.sections.7.p2")}</p>

                        <ul>
                            {getItems("TOS.sections.7.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>
                    </section>

                    {/* 8. Cancellazione del servizio */}
                    <section>
                        <h3>{t("TOS.sections.8.title")}</h3>

                        <p>{t("TOS.sections.8.p1")}</p>

                        <p>{t("TOS.sections.8.p2")}</p>

                        <ul>
                            {getItems("TOS.sections.8.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>
                    </section>

                    {/* 9. Limitazioni di responsabilità */}
                    <section>
                        <h3>{t("TOS.sections.9.title")}</h3>

                        <p>{t("TOS.sections.9.p1")}</p>

                        <ul>
                            {getItems("TOS.sections.9.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>
                    </section>

                    {/* 10. Comportamento del cliente */}
                    <section>
                        <h3>{t("TOS.sections.10.title")}</h3>

                        <p>{t("TOS.sections.10.p1")}</p>

                        <ul>
                            {getItems("TOS.sections.10.items").map(
                                (item, index) => (
                                    <li key={index}>{item}</li>
                                )
                            )}
                        </ul>
                    </section>

                    {/* 11. Modifiche ai Termini */}
                    <section>
                        <h3>{t("TOS.sections.11.title")}</h3>

                        <p>{t("TOS.sections.11.p1")}</p>
                    </section>

                    {/* 12. Contatti */}
                    <section>
                        <h3>{t("TOS.sections.12.title")}</h3>

                        <p>{t("TOS.sections.12.p1")}</p>

                        <ul className="contact-list">
                            <li>
                                <strong>
                                    {t("TOS.sections.12.email_label")}
                                </strong>{" "}
                                {t("TOS.sections.12.email")}
                            </li>

                            <li>
                                <strong>
                                    {t("TOS.sections.12.discord_label")}
                                </strong>{" "}
                                {t("TOS.sections.12.discord")}
                            </li>

                            <li>
                                <strong>
                                    {t("TOS.sections.12.website_label")}
                                </strong>{" "}
                                {t("TOS.sections.12.website")}
                            </li>
                        </ul>
                    </section>

                    {/* Footer */}
                    <div className="tos-popup__footer">
                        <span>{t("TOS.footer.lastUpdate")}</span>

                        <button type="button" onClick={onClose}>
                            {t("TOS.understood")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
